import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, CheckCheck, Pencil, AlertTriangle, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Empty } from "@/components/atlas/Shared";
import { GoldButton, GhostButton, NativeSelect } from "@/components/atlas/Fields";
import { TimesheetDialog } from "@/components/time/TimesheetDialog";
import { TsStatus } from "@/pages/employee/Hours";
import { api, downloadFile } from "@/lib/api";
import { useFetch, usePositions } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { fmtShort, fmtDate, fmtRange } from "@/lib/format";
import { fmtClock, fmtMinutes } from "@/lib/time";

const Flag = ({ k }) => {
  const { t } = useLang();
  const red = k.startsWith("missing") || k === "far_clock_out";
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider ${red ? "text-red-600" : "text-amber-700"}`}>{red && <AlertTriangle className="w-3 h-3" />}{t(`t.flag.${k}`)}</span>;
};

const Stat = ({ label, value, testid }) => (
  <div className="card-lux p-5" data-testid={testid}><div className="eyebrow">{label}</div><div className="mono text-2xl mt-2">{value}</div></div>
);

const Totals = ({ title, rows, testid }) => {
  const { t } = useLang();
  return (
    <div className="card-lux p-5" data-testid={testid}>
      <h3 className="font-display text-2xl mb-3">{title}</h3>
      {rows.length === 0 && <p className="text-sm text-neutral-500">—</p>}
      {rows.map((r) => (
        <div key={r.employee_id || r.event_id} className="flex justify-between gap-3 py-2 border-b border-white/[0.05] last:border-0 text-sm">
          <span className="truncate">{r.name}<span className="text-neutral-500 text-xs ml-2">{r.shifts} {t("t.shifts")}</span></span>
          <span className="mono text-[#D9B872] shrink-0">{fmtMinutes(r.minutes)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Timesheets() {
  const { t, lang } = useLang();
  const { label } = usePositions();
  const { data: periods } = useFetch("/pay-periods");
  const { data: employees } = useFetch("/employees");
  const { data: events } = useFetch("/events");
  const [period, setPeriod] = useState("");
  const [f, setF] = useState({ date_from: "", date_to: "", employee_id: "", event_id: "", status: "" });
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const cur = periods?.find((p) => p.current);
    if (cur && !period) {
      setPeriod(cur.start);
      setF((x) => ({ ...x, date_from: cur.start, date_to: cur.end }));
    }
  }, [periods, period]);
  const ready = !!period;
  const { data, reload } = useFetch(ready ? "/timesheets" : null, f);
  const choosePeriod = (v) => {
    setPeriod(v);
    const p = periods.find((x) => x.start === v);
    if (p) setF({ ...f, date_from: p.start, date_to: p.end });
  };
  const approvable = useMemo(() => (data?.items || []).filter((x) => x.status === "submitted").map((x) => x.id), [data]);

  const approve = async (ids) => {
    try {
      const r = await api.post("/timesheets/approve", { ids });
      toast.success(`${r.data.approved} ${t("t.approvedOk")}`);
      setSelected([]);
      reload();
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  const exportAs = (format) => downloadFile("/timesheets/export", { ...f, format }, `atlas-timesheets.${format}`).catch((e) => toast.error(errorText(t, e)));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div>
      <PageHeader eyebrow="Atlas" title={t("t.title")} sub={t("t.sub")} actions={<>
        <GhostButton data-testid="ts-add-manual" onClick={() => setEditing({})}><Plus className="w-4 h-4" />{t("t.addManual")}</GhostButton>
        <GhostButton data-testid="ts-export-csv" onClick={() => exportAs("csv")}><Download className="w-4 h-4" />CSV</GhostButton>
        <GhostButton data-testid="ts-export-xlsx" onClick={() => exportAs("xlsx")}><FileSpreadsheet className="w-4 h-4" />Excel</GhostButton>
        <GoldButton data-testid="ts-approve-selected" disabled={!selected.length} onClick={() => approve(selected)}><CheckCheck className="w-4 h-4" />{t("t.approveSel")} ({selected.length})</GoldButton>
      </>} />
      <div className="card-lux p-4 mb-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="ts-filters">
        <NativeSelect testid="ts-period-select" value={period} onChange={choosePeriod} className="col-span-2 md:col-span-1">
          {(periods || []).map((p) => <option key={p.start} value={p.start}>{fmtDate(p.start, lang, "d MMM")} – {fmtDate(p.end, lang, "d MMM yyyy")}{p.current ? " •" : ""}</option>)}
          <option value="custom">{t("t.custom")}</option>
        </NativeSelect>
        <Input data-testid="ts-date-from" type="date" value={f.date_from} onChange={(e) => { setPeriod("custom"); setF({ ...f, date_from: e.target.value }); }} className="h-10 rounded-none" />
        <Input data-testid="ts-date-to" type="date" value={f.date_to} onChange={(e) => { setPeriod("custom"); setF({ ...f, date_to: e.target.value }); }} className="h-10 rounded-none" />
        <NativeSelect testid="ts-employee-select" value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })}>
          <option value="">{t("t.allEmployees")}</option>
          {(employees || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </NativeSelect>
        <NativeSelect testid="ts-event-select" value={f.event_id} onChange={(v) => setF({ ...f, event_id: v })}>
          <option value="">{t("t.allEvents")}</option>
          {(events || []).map((e) => <option key={e.id} value={e.id}>{e.date} · {e.name}</option>)}
        </NativeSelect>
        <NativeSelect testid="ts-status-select" value={f.status} onChange={(v) => setF({ ...f, status: v })}>
          <option value="">{t("t.allStatus")}</option>
          {["open", "submitted", "approved", "flagged"].map((s) => <option key={s} value={s}>{t(`t.${s}`)}</option>)}
        </NativeSelect>
      </div>
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat label={t("t.total")} value={fmtMinutes(data.total_minutes)} testid="ts-total" />
          <Stat label={t("t.entries")} value={data.items.length} testid="ts-count" />
          <Stat label={t("t.submitted")} value={approvable.length} testid="ts-to-approve" />
          <Stat label={t("t.missing")} value={data.missing.length + data.items.filter((x) => x.flags.includes("missing_clock_out")).length} testid="ts-missing-count" />
        </div>
      )}
      {data?.missing.length > 0 && (
        <section className="mb-8" data-testid="ts-missing">
          <h2 className="font-display text-2xl mb-3">{t("t.missing")}</h2>
          <div className="card-lux divide-y divide-black/[0.05]">
            {data.missing.map((m) => (
              <div key={m.assignment_id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`missing-${m.assignment_id}`}>
                <div className="min-w-0 text-sm"><span className="font-medium">{m.employee_name}</span><span className="text-neutral-500"> · {m.event_name} · {fmtShort(m.date, lang)} · {label(m.position_key)} · <span className="mono">{fmtRange(m.scheduled_start, m.scheduled_end, t)}</span></span></div>
                <div className="flex items-center gap-3"><Flag k="missing_clock_in" /><GhostButton data-testid={`missing-add-${m.assignment_id}`} onClick={() => setEditing(m)} className="h-8 text-xs"><Plus className="w-3.5 h-3.5" />{t("t.addEntry")}</GhostButton></div>
              </div>
            ))}
          </div>
        </section>
      )}
      {data && data.items.length === 0 && <Empty text={t("t.none")} testid="ts-empty" />}
      {data?.items.length > 0 && (
        <div className="card-lux overflow-x-auto mb-8" data-testid="ts-table">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="border-b border-white/[0.06] text-left">
                <th className="px-4 py-3 w-10"><Checkbox data-testid="ts-select-all" checked={approvable.length > 0 && selected.length === approvable.length} onCheckedChange={(v) => setSelected(v ? approvable : [])} /></th>
                {[t("c.name"), t("ev.title"), t("t.in"), t("t.out"), t("t.break"), t("t.total"), t("c.status"), ""].map((h, i) => <th key={i} className="eyebrow font-semibold px-4 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.items.map((x) => (
                <tr key={x.id} className="border-b border-white/[0.04] last:border-0" data-testid={`ts-row-${x.id}`}>
                  <td className="px-4 py-3">{x.status === "submitted" && <Checkbox data-testid={`ts-select-${x.id}`} checked={selected.includes(x.id)} onCheckedChange={() => toggle(x.id)} />}</td>
                  <td className="px-4 py-3"><div className="font-medium">{x.employee_name}{x.employee_deleted && <span data-testid={`ts-deleted-${x.id}`} className="ml-2 text-[10px] uppercase tracking-wider text-neutral-500">{t("emp.deletedTag")}</span>}</div><div className="flex flex-wrap gap-2 mt-1">{x.flags.map((k) => <Flag key={k} k={k} />)}</div></td>
                  <td className="px-4 py-3"><div className="truncate max-w-[220px]">{x.event_name}</div><div className="text-xs text-neutral-500 capitalize">{fmtShort(x.date, lang)} · {label(x.position_key)} · <span className="mono">{fmtRange(x.scheduled_start, x.scheduled_end, t)}</span></div></td>
                  <td className="px-4 py-3 mono">{fmtClock(x.clock_in)}{x.in_geo && <div data-testid={`ts-in-site-${x.id}`} className="text-[10px] text-neutral-500">{x.in_geo.distance_m} m{x.in_geo.site && ` · ${t(`t.site.${x.in_geo.site}`)}`}</div>}</td>
                  <td className="px-4 py-3 mono">{fmtClock(x.clock_out)}{x.out_geo && <div data-testid={`ts-out-site-${x.id}`} className="text-[10px] text-neutral-500">{x.out_geo.distance_m ?? "?"} m{x.out_geo.site && ` · ${t(`t.site.${x.out_geo.site}`)}`}</div>}</td>
                  <td className="px-4 py-3 mono">{x.break_minutes || 0} min</td>
                  <td className="px-4 py-3 mono text-[#D9B872]" data-testid={`ts-total-${x.id}`}>{fmtMinutes(x.total_minutes)}</td>
                  <td className="px-4 py-3"><TsStatus status={x.status} testid={`ts-status-${x.id}`} /></td>
                  <td className="px-4 py-3 text-right"><button data-testid={`ts-edit-${x.id}`} onClick={() => setEditing(x)} className="press p-2 rounded-full border border-white/10 hover:border-[#C5A059]" aria-label={t("t.edit")}><Pencil className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Totals title={t("t.byEmployee")} rows={data.by_employee} testid="ts-by-employee" />
          <Totals title={t("t.byEvent")} rows={data.by_event} testid="ts-by-event" />
        </div>
      )}
      <TimesheetDialog entry={editing} onClose={() => setEditing(null)} onSaved={reload} events={events} employees={employees} />
    </div>
  );
}
